import { Components, Theme } from '@mui/material/styles';

// ─── Typography ───────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontSize: '2.5rem', fontWeight: 500 },
  h2: { fontSize: '2rem',   fontWeight: 500 },
  h3: { fontSize: '1.75rem', fontWeight: 500 },
  h4: { fontSize: '1.5rem', fontWeight: 500 },
  h5: { fontSize: '1.25rem', fontWeight: 500 },
  h6: { fontSize: '1rem',   fontWeight: 600 },
};

// MUI базовий розмір шрифту (впливає на всі rem-значення в темі)
// normal: 14px (MUI default), compact: 12px
export const fontSizeScale: Record<'normal' | 'compact', number> = {
  normal:  14,
  compact: 12,
};

// ─── Shape ────────────────────────────────────────────────────────────────────

export const shape = {
  borderRadius: 8,        // MUI default override
  borderRadiusLg: 12,     // Cards, Drawers
};

// ─── Shadows ──────────────────────────────────────────────────────────────────

export const shadows = {
  card: '0 2px 12px rgba(0,0,0,0.08)',
  drawer: '2px 0 12px rgba(0,0,0,0.08)',
};

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export const sidebar = {
  // Light modes — MUI system colors, auto-adapt to dark/light theme
  lightBg:   'background.paper',
  lightText: 'text.primary',

  // Dark mode brand backgrounds
  adminBg:     '#4a148c',
  developerBg: '#263238',
  expertBg:    '#d8f3d1',
  pmBg:        '#0d47a1',
  ownerBg:     '#4e342e',

  // Dark sidebar text & overlays (white-on-dark)
  darkText:            'common.white',
  darkDivider:         'rgba(255,255,255,0.12)',
  darkSubheaderText:   'rgba(255,255,255,0.7)',
  darkHover:           'rgba(255,255,255,0.08)',
  darkHoverMd:         'rgba(255,255,255,0.1)',
  darkSelected:        'rgba(255,255,255,0.15)',
  darkSelectedHover:   'rgba(255,255,255,0.25)',
  darkButtonBg:        'rgba(255,255,255,0.1)',
  darkButtonBgHover:   'rgba(255,255,255,0.2)',
  darkBorder:          'rgba(255,255,255,0.3)',
  darkBorderHover:     'rgba(255,255,255,0.5)',

  // Tooltip background for light-sidebar mode (dark tooltip for contrast)
  tooltipBgLight: '#1e293b',
};

// ─── Event-type accent colors ─────────────────────────────────────────────────

export const eventColors = {
  food:       { main: '#ff8f00', dark: '#e65100' },
  foodExpert: { main: '#6EB52C', dark: '#5da025' },
  workout:    { main: '#d32f2f', dark: '#b71c1c' },
  medication: { main: '#388e3c', dark: '#2e7d32' },
  water:      { main: '#0288d1', dark: '#0277bd' },
  feeling:    { main: '#7b1fa2', dark: '#6a1b9a' },
  task:       { main: '#00796b', dark: '#00695c' },
};

// ─── Calendar event card (client calendar, expert zone) ────────────────────────
// Картка події та смуга виконання призначення експерта.

export const calendarEventCard = {
  bg:         { light: '#F6F6F8', dark: 'rgba(255,255,255,0.06)' },
  border:     { light: '#E5E7EB', dark: 'rgba(255,255,255,0.12)' },
  completed:  '#6EB52C',
  missed:     '#FF7070',
  statusText: '#FFFFFF',
};

// Смуга виконання призначень у заголовку дня.
export const dayTaskProgress = {
  track: { light: '#DCE2E9', dark: 'rgba(255,255,255,0.12)' },
  fill:  '#6EB52C',
};

// ─── Nutrients ────────────────────────────────────────────────────────────────
// Підсумок дня календаря клієнта - ряд карток Б/Ж/В/Ккал (значення + % від денної цілі),
// під смугою прогресу виконання задач. Figma-макет, без dark-варіанту (не задано дизайном).

export const dayNutrientBox = {
  background: '#FFFFFF',
  text:       '#26313F',
  textMuted:  '#666666',
};

// ─── Daily norms ──────────────────────────────────────────────────────────────
// Картки денних норм клієнта (Калорії/Вода/Б/Ж/В) на дашборді клієнта в експерта. Figma-макет, без dark-варіанту.

export const dailyNormCard = {
  background:      '#FFFFFF',
  backgroundHover: '#F7FAFE',
  borderHover:     '#CBDAF1',
  shadow:          '0 2px 4px rgba(0,0,0,0.12)',
  shadowHover:     '0 2px 8px rgba(0,0,0,0.12)',
  titleIcon:       '#D83430',
  icon:            '#326BC6',
  text:            '#26313F',
  textMuted:       '#666666',
  edit:            '#A7AFB8',
  editHover:       '#74808D',
  editBgHover:     '#F0F5FC',
  // Мітка «власні» біля заголовка - ледь помітна (без макета); крапка власних норм у шапці календаря - акцент.
  ownBadgeBg:      '#F7F9FC',
  ownBadgeText:    '#C3CEDD',
  ownMarker:       '#326BC6',
};

// Модалки редагування денних норм (Figma).
export const clientProfileHeader = {
  divider:         '#D5D7D4',
  name:            '#26313F',
  text:            '#26313F',
  icon:            '#989898',
  label:           '#989898',
  badgeBg:         '#F6F8F5',
  primary:         '#6EB52C',
  primaryText:     '#FFFFFF',
  secondaryBg:     'rgba(110, 181, 44, 0.15)',
  secondaryText:   '#5D9B24',
  iconButtonBg:    '#E9F4DF',
  iconButtonIcon:  '#6EB52C',
};

export const clientGoalCard = {
  background:   '#FFFFFF',
  shadow:       '0 1px 4px rgba(12, 12, 13, 0.1), 0 1px 4px rgba(12, 12, 13, 0.05)',
  iconBg:       '#EEF6E6',
  icon:         '#6EB52C',
  label:        '#575757',
  title:        '#282828',
  chipBorder:   '#F2F2F2',
  chipText:     '#575757',
  text:         '#282828',
  textMuted:    '#575757',
  progressTrack: '#F2F2F2',
  progressFill: '#6EB52C',
};

export const collaborationPlan = {
  background:     '#FFFFFF',
  shadow:         '0 2px 4px rgba(12, 12, 13, 0.12)',
  title:          '#26313F',
  text:           '#26313F',
  divider:        '#F2F2F2',
  checkboxBorder: '#D5D7D4',
  checkboxChecked: '#6EB52C',
  menu:           '#575757',
  caption:        '#666666',
  // Підпис дати/часу під назвою задачі.
  dateCaption:    '#989898',
  // Та сама дата, якщо задача прострочена (scheduledLdt у минулому, ще pending).
  dateOverdue:    '#FF7070',
  // Кнопка "+ Додати" в шапці картки.
  addButtonBg:    'rgba(110, 181, 44, 0.15)',
  addButtonText:  '#5D9B24',
  // Футер "Переглянути всі задачі".
  footerBg:       '#F7F9FA',
  footerText:     '#666666',
};

export const recommendationsProgress = {
  background:     '#FFFFFF',
  shadow:         '0 2px 4px rgba(0, 0, 0, 0.12)',
  label:          '#666666',
  value:          '#282828',
  text:           '#26313F',
  axis:           '#989898',
  grid:           '#F2F2F2',
  barTrack:       '#FAFAFA',
  itemBg:         '#FAFAFA',
  pillBg:         '#E9F4DF',
  pillText:       '#2E7D32',
  rangeBg:        '#F6F8F5',
  rangeItemBg:    '#F7F9FA',
  rangeActiveBg:  '#EFF6E9',
  rangeActiveBorder: '#C2E2A9',
  rangeActiveText: '#5D9B24',
  // Daily bars by level: fill / value label.
  high:           { fill: '#6EB52C', text: '#5D9B24' },
  medium:         { fill: '#F2C955', text: '#D19E0F' },
  low:            { fill: '#FF7070', text: '#FE4141' },
  // Category icon: circle background (10% tint of the icon colour) / icon.
  categories: {
    water:    { bg: '#ECF7FC', icon: '#41AEE4' },
    food:     { bg: '#FFF1F1', icon: '#FF7070' },
    activity: { bg: '#FAF3FF', icon: '#D183FF' },
    feeling:  { bg: '#ECF3FE', icon: '#3C82F6' },
    // Not in the design: orange accent picked to differ from the other categories.
    medication: { bg: '#FEF4EC', icon: '#F2994A' },
    tasks:    { bg: '#F1F8EA', icon: '#6EB52C' },
  },
};

export const clientMetricsCards = {
  background: '#FFFFFF',
  shadow:     '0 2px 4px rgba(0, 0, 0, 0.12)',
  titleIcon:  '#326BC6',
  text:       '#26313F',
  label:      '#666666',
  infoIcon:   '#B1B5AD',
};

export const dailyNormEditDialog = {
  shadow:              '0 16px 48px rgba(36,52,74,0.14)',
  divider:             '#DCE2E9',
  close:               '#666666',
  inputBorder:         '#74808D',
  recommendedBg:       '#F7F9FA',
  recommendedCalories: '#6EB52C',
  recommendedWater:    '#3FA9E1',
  protein:             '#6EB52C',
  fat:                 '#F2C955',
  carbs:               '#D183FF',
  accent:              '#326BC6',
  accentHover:         '#2A5BAA',
  accentDisabled:      '#C5D6F0',
  text:                '#26313F',
  textMuted:           '#666666',
};

// ─── Metric explanation ───────────────────────────────────────────────────────
// Вікно пояснення розрахунку метрики (BMR / WDNA / AMDR) з модалок денних норм (Figma).

export const metricExplanationDialog = {
  divider: '#D5D7D4',
  text:    '#282828',
  link:    '#326BC6',
};

// ─── Toast ────────────────────────────────────────────────────────────────────
// Іконка тосту успіху (Figma). Фон/текст самого тосту - у index.css (стилі react-toastify).

export const toastSuccess = {
  background: '#6EB52C',
  iconBg:     '#FFFFFF',
};

// ─── Client app preview (PM) ──────────────────────────────────────────────────
// Кольори вікон деталей мобільного додатку клієнта (app/: food-details-dialog, activity-details-dialog) для
// приблизного превʼю "як бачить клієнт" у кабінеті продакта. Додаток світлий, тому без dark-варіантів.

export const clientAppPreview = {
  frameBorder: '#d0d0d0',
  background:  '#ffffff',
  text:        '#2a2a2a',
  textStrong:  '#242424',
  textMuted:   '#6f6f6f',
  textFaint:   '#8a8a8a',
  divider:     '#eeeeee',
  cardBg:      '#fafafa',
  cardBorder:  '#eeeeee',
  food:    { accent: '#ff8500', iconBg: '#fff0df', badgeBg: '#fff0e4' },
  workout: { accent: '#43a047', iconBg: '#eaf7ec', badgeBg: '#eaf7ec' },
  macros:  { kcal: '#262626', protein: '#ef4444', fat: '#f97316', carbs: '#43a047' },
};

// ─── Component overrides ─────────────────────────────────────────────────────

export const componentOverrides: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: shape.borderRadius,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: shape.borderRadiusLg,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: shape.borderRadiusLg,
        boxShadow: shadows.card,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: 'none',
        borderRadius: 0,
        boxShadow: shadows.drawer,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: 0,
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: 52,
        '@media (min-width: 600px)': {
          minHeight: 52,
        },
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        paddingTop: 6,
        paddingBottom: 6,
      },
    },
  },

  // ─── Form controls — consistent size & variant across the project ───────────
  MuiTextField: {
    defaultProps: {
      size: 'small',
      variant: 'outlined',
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: 'small',
      variant: 'outlined',
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: false,
    },
  },
  MuiSelect: {
    defaultProps: {
      size: 'small',
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      size: 'small',
    },
  },
};
